import {
  NumberInput as ChakraNumberInput,
  NumberInputProps as ChakraNumberInputProps,
  HStack,
  Hide,
  InputGroup,
  InputLeftElement,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInputField,
  NumberInputStepper,
  Show,
  useNumberInput
} from '@chakra-ui/react';
import { MutableRefObject, useState } from 'react';
import { Icon, IconName } from '../../medias';
import { Button } from '../button/button';
import { Input } from '../input/input';
export {
  NumberInput as ChakraNumberInput,
  NumberInputField as ChakraNumberInputField,
  useNumberInput
} from '@chakra-ui/react';
export type {
  NumberDecrementStepperProps,
  NumberIncrementStepperProps,
  NumberInputFieldProps,
  NumberInputStepperProps
} from '@chakra-ui/react';

export type NumberInputProps = ChakraNumberInputProps & {
  withStepper?: boolean;
  leftElement?: JSX.Element;
  ref?: MutableRefObject<HTMLInputElement | null>;
  placeholder?: string;
};
export function NumberInput({
  children,
  leftElement,
  withStepper = false,
  step = 0.01,
  ref,
  ...rest
}: NumberInputProps & {
  withStepper?: boolean;
  leftElement?: JSX.Element;
  step?: number;
  ref?: MutableRefObject<HTMLInputElement | null>;
}): JSX.Element {
  const [leftElWidth, setLeftElWidth] = useState<number | null>(null);
  const parseValue = (value: string): string => {
    return value.replace(',', '.');
  };
  const {
    defaultValue,
    max,
    min,
    onChange,
    placeholder,
    precision,
    clampValueOnBlur
  } = rest;

  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
    useNumberInput({
      defaultValue,
      step,
      min,
      max,
      onChange,
      clampValueOnBlur: clampValueOnBlur ?? true,
      precision: precision ?? 2
    });

  const inc = getIncrementButtonProps();
  const dec = getDecrementButtonProps();
  const input = getInputProps({ inputMode: 'decimal' });

  return (
    <InputGroup>
      {leftElement ? (
        <InputLeftElement
          h="100%"
          ref={(el): void => {
            if (!el) {
              return;
            }
            setLeftElWidth(el.clientWidth);
          }}
        >
          {leftElement}
        </InputLeftElement>
      ) : null}

      <Show below="md">
        <HStack spacing={1} minWidth={'space_unit.x16'}>
          <Button
            data-testid="increment-stepper-mobile"
            variant={'outline'}
            padding={2}
            backgroundColor={'palette_gray.70'}
            {...inc}
          >
            <Icon name={IconName.ArrowUp} width={'0.5em'} height={'0.5em'} />
          </Button>
          <Input
            backgroundColor={'palette_white'}
            fontSize={'small'}
            {...input}
            ref={ref}
          />
          <Button
            data-testid="decrement-stepper-mobile"
            variant={'outline'}
            padding={2}
            backgroundColor={'palette_gray.70'}
            {...dec}
          >
            <Icon name={IconName.ArrowDown} width={'0.5em'} height={'0.5em'} />
          </Button>
        </HStack>
      </Show>
      <Hide below="md">
        <ChakraNumberInput
          inputMode="none"
          parse={parseValue}
          isValidCharacter={(value: string): boolean =>
            /^[Ee0-9+\-.,]$/.test(value)
          }
          pattern="[0-9]*(\.[0-9]*)?"
          precision={2}
          step={step}
          flexGrow={1}
          ref={ref}
          {...rest}
        >
          <NumberInputField
            textIndent={leftElWidth ? `${leftElWidth - 25}px` : undefined}
            minWidth="space_unit.x16"
            outline="none"
            borderColor="palette_gray.5"
            borderWidth="1px"
            borderStyle="solid"
            inputMode="decimal"
            h="48px"
            paddingX="space_unit.x2"
            borderRadius="radii.x1"
            backgroundColor={'palette_white'}
            _focusVisible={{
              outlineColor: 'palette_blue.5',
              outlineWidth: '3px',
              outlineStyle: 'solid',
              outlineOffset: 0
            }}
            _disabled={{
              color: 'palette_gray.60',
              backgroundColor: 'palette_gray.5',
              border: 'none'
            }}
            placeholder={placeholder}
          />

          {children}
          {withStepper && <NumberInput.Stepper />}
        </ChakraNumberInput>
      </Hide>
    </InputGroup>
  );
}

NumberInput.Stepper = (): JSX.Element => (
  <NumberInputStepper>
    <NumberIncrementStepper
      data-testid="increment-stepper"
      children={
        <Icon name={IconName.ArrowUp} width={'.85em'} height={'.85em'} />
      }
      border={'none'}
      bg={'palette_gray.70'}
      _active={{ bg: 'palette_gray.60' }}
    />
    <NumberDecrementStepper
      data-testid="decrement-stepper"
      children={
        <Icon name={IconName.ArrowDown} width={'.85em'} height={'.85em'} />
      }
      border={'none'}
      bg={'palette_gray.70'}
      _active={{ bg: 'palette_gray.60' }}
    />
  </NumberInputStepper>
);
